import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Input,
  Modal,
  Form,
  message,
  ConfigProvider,
  Popconfirm,
  Space,
  Segmented,
  Tag,
  Image,
  Typography,
  Descriptions,
  Avatar,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  UserOutlined,
  CarOutlined,
} from "@ant-design/icons";
import "../adminShared/adminPage.css";
import "./reports.css";
import { routes } from "../../api/routes";
import { callApi } from "../../api/apiCaller";
import Loader from "../../components/loader/loader";
import {
  reportsFromResponse,
  reportFromResponse,
  isMutationSuccess,
  getReportTypeLabel,
  isVehicleReport,
  getReportedItemSummary,
} from "../../helper/helper";
import dayjs from "dayjs";

const { Text, Paragraph } = Typography;

const tabToUrl = (tab) => {
  if (tab === "seen") {
    const q = new URLSearchParams({ currentPage: "1", pageSize: "500" });
    return `${routes.getSeenReports}?${q.toString()}`;
  }
  if (tab === "unseen") {
    const q = new URLSearchParams({ currentPage: "1", pageSize: "500" });
    return `${routes.getUnseenReports}?${q.toString()}`;
  }
  const q = new URLSearchParams({ page: "1", limit: "500" });
  return `${routes.getAllReports}?${q.toString()}`;
};

const ReportDetailBody = ({ report }) => {
  if (!report) return null;
  const target = report.report;
  const isVehicle = isVehicleReport(report);
  const summary = getReportedItemSummary(report);

  return (
    <div className="report-detail">
      <Descriptions column={1} size="small" bordered className="report-detail-meta">
        <Descriptions.Item label="Type">
          <Tag color={isVehicle ? "blue" : "purple"} icon={isVehicle ? <CarOutlined /> : <UserOutlined />}>
            {getReportTypeLabel(report)}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Reported item">{summary}</Descriptions.Item>
        <Descriptions.Item label="Reported by">
          {report.creator?.name || "—"}
        </Descriptions.Item>
        <Descriptions.Item label="Reason">
          <Paragraph style={{ margin: 0 }}>{report.description || "—"}</Paragraph>
        </Descriptions.Item>
        <Descriptions.Item label="Submitted">
          {report.createdAt ? dayjs(report.createdAt).format("DD MMM YYYY, HH:mm") : "—"}
        </Descriptions.Item>
      </Descriptions>

      {!target ? (
        <Text type="secondary" className="report-detail-missing">
          The reported item is no longer available (it may have been deleted).
        </Text>
      ) : isVehicle ? (
        <div className="report-detail-vehicle">
          {target.images?.[0] && (
            <Image
              src={target.images[0]}
              alt={summary}
              className="report-detail-image"
              style={{ maxWidth: 280, borderRadius: 8 }}
            />
          )}
          <Descriptions column={1} size="small" title="Vehicle details">
            <Descriptions.Item label="Make / model">
              {[target.year, target.make, target.model, target.modelVariant].filter(Boolean).join(" ")}
            </Descriptions.Item>
            <Descriptions.Item label="Registration">{target.registration || "—"}</Descriptions.Item>
            <Descriptions.Item label="Price">
              {target.price != null ? `£${Number(target.price).toLocaleString()}` : "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Status">{target.status || "—"}</Descriptions.Item>
            <Descriptions.Item label="Listed by">
              {target.creator?.name || "—"}
              {target.creator?.email ? ` (${target.creator.email})` : ""}
            </Descriptions.Item>
          </Descriptions>
        </div>
      ) : (
        <div className="report-detail-user">
          <Space align="start">
            <Avatar src={target.image} icon={<UserOutlined />} size={64} />
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Name">{target.name || "—"}</Descriptions.Item>
              <Descriptions.Item label="Email">{target.email || "—"}</Descriptions.Item>
              <Descriptions.Item label="Phone">{target.number || "—"}</Descriptions.Item>
            </Descriptions>
          </Space>
        </div>
      )}
    </div>
  );
};

const Reports = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("unseen");
  const [editOpen, setEditOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailReport, setDetailReport] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const load = () => {
    callApi(
      "GET",
      tabToUrl(tab),
      null,
      setLoading,
      (res) => {
        if (res.status !== 200) return;
        setRows(reportsFromResponse(res));
      },
      () => message.error("Failed to load reports")
    );
  };

  useEffect(() => {
    load();
  }, [tab]);

  const openDetail = (row) => {
    setDetailOpen(true);
    setDetailReport(null);
    setDetailLoading(true);
    callApi(
      "GET",
      `${routes.getOneReport}/${row._id}`,
      null,
      setDetailLoading,
      (res) => {
        if (res.status === 200) {
          setDetailReport(reportFromResponse(res) || row);
          load();
        } else {
          setDetailReport(row);
        }
      },
      () => {
        message.error("Failed to load report details");
        setDetailReport(row);
      }
    );
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setDetailReport(null);
  };

  const openEdit = (row) => {
    setEditing(row);
    form.setFieldsValue({ description: row.description || "" });
    setEditOpen(true);
  };

  const submit = (values) => {
    if (!editing?._id) return;
    setEditOpen(false);
    const id = editing._id;
    setEditing(null);
    form.resetFields();

    callApi(
      "PATCH",
      `${routes.updateReport}/${id}`,
      { description: values.description },
      setLoading,
      (res) => {
        if (isMutationSuccess(res)) {
          message.success("Report updated");
          load();
        }
      },
      () => message.error("Failed to update report")
    );
  };

  const remove = (id) => {
    callApi(
      "DELETE",
      `${routes.deleteReport}/${id}`,
      null,
      setLoading,
      (res) => {
        if (isMutationSuccess(res)) {
          message.success("Report deleted");
          load();
        }
      },
      () => message.error("Failed to delete report")
    );
  };

  const columns = [
    { title: "No", key: "i", width: 50, render: (_, __, i) => i + 1 },
    {
      title: "Type",
      key: "reportType",
      width: 100,
      render: (_, row) => {
        const isVehicle = isVehicleReport(row);
        return (
          <Tag color={isVehicle ? "blue" : "purple"}>
            {getReportTypeLabel(row)}
          </Tag>
        );
      },
    },
    {
      title: "Reported item",
      key: "reportedItem",
      width: 220,
      ellipsis: true,
      render: (_, row) => (
        <Text strong>{getReportedItemSummary(row)}</Text>
      ),
    },
    {
      title: "Reported by",
      key: "creator",
      width: 140,
      ellipsis: true,
      render: (_, row) => row.creator?.name || "—",
    },
    {
      title: "Reason",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
    },
    {
      title: "Status",
      key: "isSeen",
      width: 90,
      render: (_, row) =>
        row.isSeen === false ? (
          <Tag color="orange">Unread</Tag>
        ) : (
          <Tag color="default">Seen</Tag>
        ),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 130,
      render: (d) => (d ? dayjs(d).format("DD MMM YYYY HH:mm") : "—"),
    },
    {
      title: "Actions",
      key: "a",
      width: 160,
      fixed: "right",
      render: (_, row) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => openDetail(row)}
          >
            View
          </Button>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row)} />
          <Popconfirm title="Delete this report?" onConfirm={() => remove(row._id)}>
            <Button danger size="small" icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <ConfigProvider theme={{ token: { colorPrimary: "#555BDF" } }}>
      <div className="admin-entity-page">
        <Loader loading={loading || detailLoading} />
        <div className="admin-entity-header">
          <h1 className="admin-entity-title">Reports</h1>
        </div>
        <div className="admin-entity-card">
          <div className="admin-entity-actions">
            <Segmented
              options={[
                { label: "Unread", value: "unseen" },
                { label: "All", value: "all" },
                { label: "Seen", value: "seen" },
              ]}
              value={tab}
              onChange={(v) => setTab(v)}
            />
          </div>
          <Table
            className="admin-entity-table"
            rowKey="_id"
            columns={columns}
            dataSource={rows}
            scroll={{ x: 1100 }}
            pagination={{ pageSize: 20, showSizeChanger: true }}
          />
        </div>

        <Modal
          title="Report details"
          open={detailOpen}
          onCancel={closeDetail}
          width={640}
          destroyOnClose
          footer={
            <Button type="primary" onClick={closeDetail}>
              Close
            </Button>
          }
        >
          {detailLoading && !detailReport ? (
            <Text type="secondary">Loading…</Text>
          ) : (
            <ReportDetailBody report={detailReport} />
          )}
        </Modal>

        <Modal
          title="Edit report"
          open={editOpen}
          onCancel={() => {
            setEditOpen(false);
            setEditing(null);
            form.resetFields();
          }}
          onOk={() => form.submit()}
          destroyOnClose
        >
          <Form form={form} layout="vertical" onFinish={submit}>
            <Form.Item name="description" label="Description">
              <Input.TextArea rows={4} />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </ConfigProvider>
  );
};

export default Reports;
